;(() => {
  window.PRODUCT_CACHE = window.PRODUCT_CACHE || {}
  window.PRODUCT_LOADING = window.PRODUCT_LOADING || new Set()
  window.PRODUCT_FAILED = window.PRODUCT_FAILED || new Set()
  let currentVariant = null,
    currentProductHandle = '',
    isFirstLoaded = false,
    originalTitle = '',
    originalTagline = '',
    persistentTitle = false,
    persistentTagline = false

  if (!customElements.get('product-collection-selector')) {
    class ProductCollectionSelector extends HTMLElement {
      constructor() {
        super()
        this.main = this.closest('section[data-main-product]')
        this.wrapper = this.closest('[data-component="product/buy-panel"]')
        this.form = this.closest('form')
        this.selector = this.querySelector('[data-product-selector]')

        this.init()

        this.addEventListener('change', this.onOptionChange.bind(this))
        this.addEventListener('click', this.onImageSwatchClick.bind(this))
        document.addEventListener('product:variant:change', this.saveSelectedVariant.bind(this))

        // Track option changes for localStorage
        document.addEventListener('change', (e) => {
          if (e.target.matches('input[type="radio"][data-hook="product-option"]')) {
            // Update localStorage whenever user changes an option
            const optionName = e.target.name
            const optionValue = e.target.value

            const STORAGE_KEY = 'lilac_selected_variants'
            const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
            stored[optionName] = optionValue
            localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
          }
        })
      }

      init() {
        // Get product list from section data
        const section = this.closest('[data-product-collection]')
        const productList = JSON.parse(section?.getAttribute('data-product-list') || '[]')

        // Store original title and tagline on first load (image swatches keep this kit title)
        const titleElement = document.querySelector('.product-collection-title')
        const taglineElement = document.querySelector('.product-collection-tagline')

        if (!originalTitle && titleElement) {
          originalTitle = titleElement.textContent.trim()
        }
        if (!originalTagline && taglineElement) {
          originalTagline = taglineElement.textContent.trim()
        }

        persistentTitle = section?.getAttribute('data-persistent-title') === 'true'
        persistentTagline = section?.getAttribute('data-persistent-tagline') === 'true'

        if (this.selector && productList.length > 0) {
          // Aggressive preloading for all products
          const urlsToPreload = []

          Array.from(this.selector.options).forEach((option) => {
            const productHandle = option.value
            const url = `/products/${productHandle}`
            urlsToPreload.push({ url, option })
          })

          // Preload all URLs immediately with retry mechanism
          urlsToPreload.forEach(({ url, option }, index) => {
            // Set up layout promise
            option.layout = this.aggressivePreload(url)

            // Stagger initial requests slightly
            setTimeout(() => {
              this.aggressivePreload(url)
            }, index * 30) // 30ms stagger
          })
        }

        isFirstLoaded = true
      }

      async aggressivePreload(url, retryCount = 0) {
        const maxRetries = 3

        // Skip if already cached, loading, or failed too many times
        if (
          window.PRODUCT_CACHE[url] ||
          window.PRODUCT_LOADING.has(url) ||
          (window.PRODUCT_FAILED.has(url) && retryCount >= maxRetries)
        ) {
          return window.PRODUCT_CACHE[url]
        }

        // Mark as loading
        window.PRODUCT_LOADING.add(url)

        try {
          const result = await this.getProductLayoutWithRetry(url, retryCount)
          window.PRODUCT_LOADING.delete(url)
          window.PRODUCT_FAILED.delete(url) // Clear failed status on success
          return result
        } catch (error) {
          window.PRODUCT_LOADING.delete(url)

          if (retryCount < maxRetries) {
            // Exponential backoff retry
            const delay = Math.pow(2, retryCount) * 1000
            setTimeout(() => {
              this.aggressivePreload(url, retryCount + 1)
            }, delay)
          } else {
            window.PRODUCT_FAILED.add(url)
            console.warn(`Failed to preload ${url} after ${maxRetries} retries`)
          }
        }
      }

      saveSelectedVariant(evt) {
        currentVariant = evt.detail.variant
      }

      updateUrlWithProduct(productHandle) {
        const url = new URL(window.location)
        url.searchParams.set('product_query', productHandle)
        window.history.replaceState({}, '', url)
      }

      restoreSelectedProductOptions(selectedOptions) {
        if (!selectedOptions || Object.keys(selectedOptions).length === 0) return

        Object.keys(selectedOptions).forEach((optionName) => {
          const selectedValue = selectedOptions[optionName]

          // Find all radio buttons for this option
          const allRadios = document.querySelectorAll(
            `[data-component="product/buy-panel"] input[type="radio"][data-hook="product-option"]`
          )

          // Check if the desired value exists and is available
          let valueExists = false
          let valueAvailable = false

          allRadios.forEach((radio) => {
            if (radio.name === optionName && radio.value === selectedValue) {
              valueExists = true
              if (!radio.classList.contains('disabled') && !radio.disabled) {
                valueAvailable = true
              }
            }
          })

          // Only restore if the value exists AND is available
          if (valueExists && valueAvailable) {
            allRadios.forEach((radio) => {
              if (radio.name === optionName && radio.value === selectedValue) {
                radio.checked = true
                // Trigger change event to update variant
                radio.dispatchEvent(new Event('change', { bubbles: true }))
              }
            })
          } else if ((valueExists && !valueAvailable) || !valueExists) {
            // Get all available options for this option name
            const availableRadios = Array.from(allRadios).filter(
              (radio) => radio.name === optionName && !radio.classList.contains('disabled') && !radio.disabled
            )

            if (availableRadios.length > 0) {
              let selectedRadio = null

              // Priority 1: Check if any option is already checked (default from Liquid)
              const defaultChecked = availableRadios.find((radio) => radio.checked)
              if (defaultChecked) {
                selectedRadio = defaultChecked
              } else {
                // Priority 2: Try to find the default by looking for data-default attribute or similar
                const defaultRadio = availableRadios.find(
                  (radio) =>
                    radio.hasAttribute('data-default') ||
                    radio.getAttribute('data-default') === 'true' ||
                    radio.classList.contains('default')
                )

                if (defaultRadio) {
                  selectedRadio = defaultRadio
                } else {
                  // Priority 3: Sort by value and select the first one (lowest)
                  const sortedRadios = availableRadios.sort((a, b) => {
                    // Try to parse as numbers for proper sorting
                    const aNum = parseFloat(a.value)
                    const bNum = parseFloat(b.value)

                    if (!isNaN(aNum) && !isNaN(bNum)) {
                      return aNum - bNum
                    }
                    // If not numbers, sort alphabetically
                    return a.value.localeCompare(b.value)
                  })

                  selectedRadio = sortedRadios[0]
                }
              }

              if (selectedRadio) {
                selectedRadio.checked = true
                selectedRadio.dispatchEvent(new Event('change', { bubbles: true }))
              }
            }
          }
        })
      }

      async onOptionChange(event) {
        const input = event.target
        if (!input.matches('[data-product-selector]')) return

        const selectedValue = event.target.value
        currentProductHandle = selectedValue

        // Add loading state
        input.disabled = true
        input.classList.add('loading')

        // Get the product ID from the selected option
        const selectedOption = input.selectedOptions[0]
        const productId = selectedOption ? selectedOption.getAttribute('data-product-id') : null

        // Update URL with the product ID
        if (productId) {
          this.updateUrlWithProduct(productId)
        }

        const productSection = this.closest('[data-main-product]')
        const reviewsSection = document.getElementById('stamped-main-widget')

        if (!selectedOption) {
          input.disabled = false
          input.classList.remove('loading')
          return
        }

        const originalOptions = Array.from(this.selector.options).map((option) => ({
          value: option.value,
          text: option.textContent.replace(' 3-Pack', ''),
          url: option.getAttribute('data-product-url'),
          productId: option.getAttribute('data-product-id'),
          layout: option.layout,
        }))
        const imageSwatchState = this.getImageSwatchState()

        try {
          // Use aggressive retry for dropdown changes too
          let [newProductLayout, newReviewsLayout] = await this.getProductLayoutWithRetry(
            `/products/${selectedValue}`,
            0
          )

          // Modify the fetched HTML before insertion to prevent flickering
          const tempDiv = document.createElement('div')
          tempDiv.innerHTML = newProductLayout

          // Get current section settings and apply them to the new content
          const currentSection = this.closest('[data-product-collection]')
          const currentSettings = JSON.parse(currentSection?.getAttribute('data-section-settings') || '{}')

          // Preserve original title and tagline if persistent settings are enabled
          if (currentSettings.persistent_title && originalTitle) {
            const titleElement = tempDiv.querySelector('.product-collection-title')
            if (titleElement) {
              titleElement.textContent = originalTitle
            }
          }

          if (currentSettings.persistent_tagline && originalTagline) {
            const taglineElement = tempDiv.querySelector('.product-collection-tagline')
            if (taglineElement) {
              taglineElement.textContent = originalTagline
            }
          }

          if (imageSwatchState) {
            tempDiv.querySelector('[data-hook="lash-styles-label"]')?.remove()
          }

          // Update the section's data attributes with current settings
          const newSection = tempDiv.querySelector('[data-product-collection]')
          if (newSection) {
            newSection.setAttribute('data-persistent-title', currentSettings.persistent_title || 'false')
            newSection.setAttribute('data-persistent-tagline', currentSettings.persistent_tagline || 'false')
            newSection.setAttribute('data-section-settings', JSON.stringify(currentSettings))
          }

          if (imageSwatchState) {
            this.applyImageSwatchState(tempDiv.querySelector('product-collection-selector'), imageSwatchState)
            this.applyImageCardLayout(tempDiv)
          }

          const savedTab = window.lilacGetSubPurchaseTab?.()
          if (savedTab && window.lilacApplySubPurchaseTab) {
            tempDiv.querySelectorAll('.sub-purchase-tabs').forEach((root) => {
              window.lilacApplySubPurchaseTab(root, savedTab)
            })
          }

          newProductLayout = tempDiv.innerHTML

          productSection.outerHTML = newProductLayout
          if (reviewsSection && newReviewsLayout) {
            reviewsSection.outerHTML = newReviewsLayout
          }

          const newDropdown = document.querySelector('[data-product-selector]')
          if (newDropdown) {
            newDropdown.innerHTML = ''

            originalOptions.forEach((optionData) => {
              const option = document.createElement('option')
              option.value = optionData.value
              option.textContent = optionData.text.replace(' 3-Pack', '')
              option.setAttribute('data-product-url', optionData.url)
              option.setAttribute('data-product-id', optionData.productId)
              // Preserve the original selection logic - only select if it matches the current selection
              option.selected = optionData.value === selectedValue
              option.layout = optionData.layout
              newDropdown.appendChild(option)
            })
          }

          this.restoreImageSwatchState(imageSwatchState)
          this.syncImageSwatchSelection(selectedValue)

          const newSwatches = document.querySelectorAll('button[data-swatch-url]')
          newSwatches.forEach((button) => {
            const url = button.getAttribute('data-swatch-url')
            const handle = url?.split('/products/')[1]?.split('?')[0]

            if (handle === selectedValue) {
              const li = button.closest('li')
              if (li) {
                li.classList.add('on')
              }
            }
          })

          document.dispatchEvent(
            new CustomEvent('product:collection:change', {
              detail: { handle: selectedValue, productHandle: selectedValue, source: 'dropdown' },
            })
          )

          if (typeof StampedFn !== 'undefined') {
            StampedFn.reloadUGC()
          }
          if (typeof window.setFormListeners === 'function') {
            window.setFormListeners()
          }

          // Reinitialize price components after DOM replacement
          const priceElements = document.querySelectorAll('[data-hook="product-price"]')
          priceElements.forEach((priceElement) => {
            // Trigger a custom event to reinitialize the price component
            priceElement.dispatchEvent(new CustomEvent('price:reinitialize'))
          })

          // Restore previously selected product options/variants AFTER setFormListeners
          // Use requestAnimationFrame to ensure it happens after all other scripts
          requestAnimationFrame(() => {
            const STORAGE_KEY = 'lilac_selected_variants'
            const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
            this.restoreSelectedProductOptions(stored)
            window.lilacRestoreSubPurchaseTabs?.({ emitChange: true })
          })
        } catch (error) {
          console.error('Error switching product:', error)
          // Reset dropdown selection on error
          input.value = currentProductHandle
        } finally {
          // Remove loading state
          input.disabled = false
          input.classList.remove('loading')
        }
      }

      onImageSwatchClick(event) {
        const card = event.target.closest('[data-image-swatch]')
        if (!card || !this.contains(card)) return
        if (!this.selector || this.selector.disabled) return
        if (card.getAttribute('aria-pressed') === 'true') return

        const productId = card.getAttribute('data-product-id')
        const productHandle = card.getAttribute('data-product-handle')
        const match = Array.from(this.selector.options).find((option) => {
          return option.getAttribute('data-product-id') === productId || option.value === productHandle
        })

        if (!match) {
          if (!productHandle) return

          const option = document.createElement('option')
          option.value = productHandle
          option.textContent = card.textContent.trim().replace(/\s+/g, ' ')
          option.setAttribute('data-product-url', `/products/${productHandle}`)
          if (productId) option.setAttribute('data-product-id', productId)
          this.selector.appendChild(option)
          this.selector.value = productHandle
          this.selector.dispatchEvent(new Event('change', { bubbles: true }))
          return
        }

        this.selector.value = match.value
        this.selector.dispatchEvent(new Event('change', { bubbles: true }))
      }

      getImageSwatchState() {
        if (!this.hasAttribute('data-image-swatches')) return null

        const grid = this.querySelector('[data-image-swatch-grid]')
        const style = this.querySelector('[data-image-swatch-style]')
        if (!grid) return null

        return {
          gridHtml: grid.outerHTML,
          styleHtml: style ? style.outerHTML : '',
        }
      }

      applyImageSwatchState(root, state) {
        if (!root || !state?.gridHtml) return

        root.setAttribute('data-image-swatches', '')
        root.querySelectorAll('[data-image-swatch-grid], [data-image-swatch-style]').forEach((el) => el.remove())
        root.insertAdjacentHTML('beforeend', `${state.gridHtml}${state.styleHtml}`)
      }

      applyImageCardLayout(root = document) {
        const panel = root.querySelector('[data-hook="buy-panel"]')
        if (panel) {
          panel.classList.remove('px-5')
        }

        const inner = root.querySelector('[data-hook="buy-panel-image-cards"]') || panel?.firstElementChild
        if (inner) {
          inner.setAttribute('data-hook', 'buy-panel-image-cards')
          inner.classList.remove('lg:pr-[82px]')
          inner.classList.add('px-5', 'pb-5', 'lg:pl-[6px]', 'lg:pr-[82px]')
        }

        // Spacing belongs on the outer info wrapper, not the title/price flex row.
        // `.product-collection-title`'s closest div is the flex row — targeting that
        // caused a layout jump (extra gap) only after switching image swatches.
        const titleEl = root.querySelector('.product-collection-title')
        const titleRow = titleEl?.parentElement
        const info =
          root.querySelector('[data-hook="product-info-collection"]') || titleRow?.parentElement

        titleRow?.classList.remove(
          'max-lg:mb-[20px]',
          'max-lg:mt-4',
          'lg:pb-8',
          'max-lg:mb-[18px]',
          'max-lg:mt-3',
          'lg:pb-5'
        )

        if (info) {
          info.classList.remove('max-lg:mb-[18px]', 'max-lg:mt-3', 'lg:pb-5')
          // Keep pb-5 in sync with product-info-collection.liquid (use_image_cards branch)
          info.classList.add('max-lg:mb-[20px]', 'max-lg:mt-4', 'lg:pb-8', 'pb-5')
        }

        const selectorWrapper = root.querySelector('product-collection-selector')?.parentElement
        if (selectorWrapper) {
          selectorWrapper.classList.remove('md:mt-4')
          selectorWrapper.classList.add('md:mt-8')
        }

        root.querySelector('[data-hook="lash-styles-label"]')?.remove()
      }

      restoreImageSwatchState(state) {
        if (!state) return
        this.applyImageSwatchState(document.querySelector('product-collection-selector'), state)
        this.applyImageCardLayout(document)
      }

      syncImageSwatchSelection(selectedHandle) {
        const cards = document.querySelectorAll('[data-image-swatch]')
        if (!cards.length) return

        const selectedOption = document.querySelector('[data-product-selector] option:checked')
        const selectedId = selectedOption?.getAttribute('data-product-id')
        const handle = selectedHandle || selectedOption?.value

        cards.forEach((card) => {
          const isSelected =
            card.getAttribute('data-product-handle') === handle || card.getAttribute('data-product-id') === selectedId
          card.classList.toggle('is-selected', isSelected)
          card.classList.toggle('border-[#121212]', isSelected)
          card.setAttribute('aria-pressed', isSelected ? 'true' : 'false')
        })
      }

      async getProductLayout(url) {
        return this.getProductLayoutWithRetry(url, 0)
      }

      async getProductLayoutWithRetry(url, retryCount = 0) {
        if (window.PRODUCT_CACHE[url]) return window.PRODUCT_CACHE[url]

        const timeout = 10000 + retryCount * 5000 // Increase timeout with retries
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), timeout)

        try {
          // Use Shopify's section rendering API with current section settings
          const currentSection = this.closest('[data-product-collection]')
          const productHandle = url.split('/products/')[1]?.split('?')[0]

          // Build the section URL with current settings
          const sectionUrl = `${url}?section_id=product-main-collection&product_query=${productHandle}`

          // Parallel fetching with timeout and retry logic
          const [sectionResponse, productResponse] = await Promise.all([
            fetch(sectionUrl, {
              signal: controller.signal,
              headers: {
                'Cache-Control': 'no-cache',
                Pragma: 'no-cache',
              },
            }),
            fetch(url, {
              signal: controller.signal,
              headers: {
                'Cache-Control': 'no-cache',
                Pragma: 'no-cache',
              },
            }),
          ])

          clearTimeout(timeoutId)

          if (!sectionResponse.ok || !productResponse.ok) {
            throw new Error(`HTTP ${sectionResponse.status} / ${productResponse.status}`)
          }

          const [sectionHtml, productHtml] = await Promise.all([sectionResponse.text(), productResponse.text()])

          const productDom = new DOMParser().parseFromString(productHtml, 'text/html')
          const reviewsSection = productDom.getElementById('stamped-main-widget')

          window.PRODUCT_CACHE[url] = [sectionHtml, reviewsSection?.outerHTML || '']
          return window.PRODUCT_CACHE[url]
        } catch (error) {
          clearTimeout(timeoutId)

          if (retryCount < 2 && (error.name === 'AbortError' || error.message.includes('HTTP'))) {
            // Retry on timeout or HTTP errors
            await new Promise((resolve) => setTimeout(resolve, 1000 * (retryCount + 1)))
            return this.getProductLayoutWithRetry(url, retryCount + 1)
          }

          throw error
        }
      }
    }

    customElements.define('product-collection-selector', ProductCollectionSelector)
  }
})()
